import { OrderPreferences } from '@choco-admin/types';
import {
  CountryCode,
  isValidPhoneNumber,
  getPhoneUtil,
} from '@choco-admin/utils';
import * as yup from 'yup';

import { BuyerSelectOption } from '../../BuyerLinking/types';

export type LoginMethod = 'phone' | 'email' | 'emailAndPhone' | 'federated';

export interface UserFormData {
  name: string;
  email?: string;
  phone?: string;
  countryCode?: CountryCode;
  locale: string; // Required field
  isTest?: boolean;
  supplier?: boolean;
  supplierId?: string;
  supplierName?: string;
  loginMethod: LoginMethod; // Required field
  supportLogin?: boolean;
  linkedBuyers?: BuyerSelectOption[];
  orderPreferences?: OrderPreferences;
  // Country codes for order preferences phone inputs
  whatsAppCountryCode?: CountryCode;
  smsCountryCode?: CountryCode;
  // newImage?: HTMLCanvasElement | null; // Removed to avoid TypeScript hang
}

// `federated` (SSO) users log in via their identity provider, but the admin
// still supplies the email used as `loginEmail` at creation time.
const isEmailLoginMethod = (method?: LoginMethod) =>
  ['email', 'emailAndPhone', 'federated'].includes(method || 'phone');

const isPhoneLoginMethod = (method?: LoginMethod) =>
  ['phone', 'emailAndPhone'].includes(method || 'phone');

export const useUserFormValidation = () => {
  const isLoginEmailMethodSelected = (method?: LoginMethod) =>
    isEmailLoginMethod(method);

  const isLoginPhoneMethodSelected = (method?: LoginMethod) =>
    isPhoneLoginMethod(method);

  // Smart phone validation that handles both international and national formats
  const createPhoneValidation = (countryCodeField: string) =>
    yup
      .string()
      .nullable()
      .test(
        'phone-validation',
        'Please enter a valid phone number',
        function validatePhone(phone: string | null | undefined) {
          if (!phone || phone.trim() === '') {
            return true; // Optional field
          }

          try {
            // Get the country code from the form context
            const countryCode = this.parent?.[countryCodeField] as CountryCode;

            // If phone starts with +, it's international format
            if (phone.startsWith('+')) {
              // Use Google's libphonenumber to validate international numbers directly
              const phoneUtil = getPhoneUtil();
              const number = phoneUtil.parseAndKeepRawInput(phone);
              return phoneUtil.isValidNumber(number);
            }
            // National format - validate against the selected country
            return isValidPhoneNumber(phone, countryCode || 'US');
          } catch {
            return false;
          }
        }
      );

  // The order-preferences section is only rendered for distributors, so its
  // phone formats are only enforced there. Validating them for a buyer user
  // would block every save on a field they cannot see, let alone correct.
  const createOrderPreferencesSchema = (validatePhoneNumbers: boolean) =>
    yup.object({
      pdf: yup.boolean().optional(),
      xlsx: yup.boolean().optional(),
      sms: validatePhoneNumbers
        ? createPhoneValidation('smsCountryCode')
        : yup.string().nullable(),
      whatsApp: validatePhoneNumbers
        ? createPhoneValidation('whatsAppCountryCode')
        : yup.string().nullable(),
    });

  // Original validation schema without newImage to avoid TypeScript hang
  const validationSchema = yup.object({
    name: yup.string().required('Name is required'),
    email: yup
      .string()
      .email('Invalid email format')
      // Normalize on the FE so the stored email/loginEmail is canonical
      // (lowercased + trimmed) across both the create and edit forms.
      .transform((value) => value?.trim().toLowerCase() || '')
      .when('loginMethod', {
        is: (method: LoginMethod) => isEmailLoginMethod(method),
        then: (schema) => schema.required('Email is required for login'),
        otherwise: (schema) =>
          schema
            .optional()
            .nullable()
            .transform((value) => value || null),
      }),
    phone: yup.string().when('loginMethod', {
      is: (method: LoginMethod) => isPhoneLoginMethod(method),
      then: (schema) => schema.required('Phone number is required for login'),
      otherwise: (schema) => schema.optional(),
    }),
    locale: yup.string().required('Please select a language'),
    countryCode: yup.mixed<CountryCode>().optional(),
    loginMethod: yup.mixed<LoginMethod>().required('Login method is required'),
    isTest: yup.boolean().optional(),
    supplier: yup.boolean().optional(),
    linkedBuyers: yup.array().optional(),
    supplierId: yup.string().optional(),
    supplierName: yup.string().optional(),
    supportLogin: yup.boolean().optional(),
    // Country codes for phone inputs
    whatsAppCountryCode: yup.mixed<CountryCode>().optional(),
    smsCountryCode: yup.mixed<CountryCode>().optional(),
    orderPreferences: createOrderPreferencesSchema(false)
      .when('supplier', {
        is: true,
        then: () => createOrderPreferencesSchema(true),
      })
      .optional(),
    // newImage: yup.mixed<HTMLCanvasElement>().nullable().optional(), // Removed to avoid TypeScript hang
  });

  const getInputSubLabel = (isRequired: boolean) =>
    isRequired ? 'Required, used for log in' : 'Optional, for contact purposes';

  return {
    validationSchema,
    isLoginEmailMethodSelected,
    isLoginPhoneMethodSelected,
    getInputSubLabel,
  };
};
